<template>
  <div class="calcFormsForm">
    <div v-if="loading">
      <v-progress-circular
        :size="20"
        color="primary"
        class="mr-2"
        indeterminate
      ></v-progress-circular>
      Loading...
    </div>
    <div v-else-if="formItem">
      <v-form ref="formsForm" v-model="isFormValid" lazy-validation>
        <div>
          <v-text-field
            v-model="formItem.FormName"
            label="Name"
            :rules="rules"
            :readonly="!showFormButtons"
            dense
            outlined
          ></v-text-field>
        </div>
        <div>
          <v-text-field
            v-model="formItem.Headertext"
            label="Form heading"
            :readonly="!showFormButtons"
            dense
            outlined
          ></v-text-field>
        </div>
        <div>
          <v-select
            v-model="formItem.PlanId"
            label="Level"
            :items="levelsList"
            :disabled="planId === 0"
            :readonly="!showFormButtons"
            dense
            outlined
          ></v-select>
        </div>
        <div>
          <v-select
            label="Form inputs"
            v-model="formItem.Inputs"
            :items="inputsList"
            item-text="Name"
            item-value="InputId"
            :loading="loadingInputs"
            :readonly="!showFormButtons"
            return-object
            multiple
            chips
            outlined
          ></v-select>
          <div class="mb-6" v-if="planId > 0">* &#8211; Plan level inputs</div>
        </div>
      </v-form>
      <div v-if="showFormButtons" class="formFooter">
        <div class="btnContainer">
          <v-btn v-if="formId" color="error" outlined @click="onDelete" class="mr-4">
            Delete
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="onCancel" class="mr-4"> Cancel </v-btn>
          <v-btn color="primary" depressed @click="onSave"> Save </v-btn>
        </div>
      </div>
    </div>
    <div v-else>
      <v-alert type="info" outlined dense>No info found for this element</v-alert>
    </div>
    <Dialog ref="confirmationDialog" v-model="dialog"></Dialog>
  </div>
</template>

<script>
import Dialog from '@/components/layout/Dialog.vue';

export default {
  name: 'FormsForm',
  components: {
    Dialog,
  },
  props: {
    formId: { type: Number },
    planId: { type: Number },
  },

  data() {
    return {
      loading: false,
      loadingInputs: false,
      isFormValid: false,
      dialog: false,
      formItem: null,
      rules: [(value) => !!value || 'Required.'],
      availableInputs: [],
    };
  },

  computed: {
    levelsList() {
      return [
        { text: 'System', value: 0 },
        { text: 'Plan', value: this.planId },
      ];
    },

    selectedLevel() {
      if (this.formItem) {
        return this.formItem.PlanId;
      }
      return null;
    },

    inputsList() {
      const result = [...this.availableInputs];
      for (let input of this.formItem.Inputs) {
        const douplicatedInput = this.availableInputs.find(
          (item) => item.InputId === input.InputId
        );
        if (!douplicatedInput) result.push(input);
      }
      return result;
    },

    showFormButtons() {
      const userPermission = this.$store.getters.getUserPermission(this.$route.path);
      if (userPermission && userPermission == 'Full') return true;

      return false;
    },
  },

  watch: {
    formId() {
      if (this.formId) this.getForm(this.formId);
      else this.createNewForm();

      this.getAvailableInputs();
    },
    selectedLevel() {
      this.getAvailableInputs();
    },
  },

  created() {
    if (this.formId) this.getForm(this.formId);
    else this.createNewForm();

    if (this.selectedLevel) this.getAvailableInputs();
  },

  methods: {
    createNewForm() {
      this.formItem = {
        PlanId: 0,
        GroupId: 0,
        FormId: 0,
        FormName: '',
        Headertext: '',
        Inputs: [],
      };
    },

    onCancel() {
      if (this.formId) this.getForm(this.formId);
      else this.createNewForm();
    },

    onSave() {
      const isFormValid = this.$refs.formsForm.validate();
      if (isFormValid) this.saveData();
    },

    async onDelete() {
      try {
        await this.$refs.confirmationDialog.open(
          'Confirmation',
          'Are you sure you wish to delete this record?'
        );
        this.deleteForm();
      } catch (err) {
        console.log(err);
      }
    },

    async getForm() {
      this.loading = true;
      this.formItem = null;
      try {
        let response = await this.$api.get('/CalculationForms/GetInputForm?id=' + this.formId);

        if (!response.data) {
          const msg = 'Unable to load form';
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }

        var data = response.data;
        this.formItem = data;
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    async saveData() {
      this.updateForm();
    },

    async updateForm() {
      try {
        let response = await this.$api.post('/CalculationForms/UpdateInputForm', this.formItem);

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Form item is saved',
        });
        if (this.formItem.FormId === 0) {
          this.$emit('reloadList', 'update', response.data.Value);
        } else {
          this.$emit('reloadList', 'update');
          this.getForm();
        }
      } catch (err) {
        console.error(err);
      }
    },

    async deleteForm() {
      try {
        await this.$api.delete('/CalculationForms/deleteInputForm?id=' + this.formId);

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Form item is deleted',
        });
        this.$emit('reloadList', 'delete');
      } catch (err) {
        console.error(err);
      }
    },

    async getAvailableInputs() {
      this.loadingInputs = true;
      try {
        let response = await this.$api.get(
          `/Calculation/GetCalculationItems?planId=${this.planId}&groupId=0&calcType=Inputs`
        );
        var data = response.data;
        if (data.length) {
          this.availableInputs = data.map((item) => ({
            FormId: this.formId,
            InputId: item.Id,
            InputOrder: 0,
            DefaultValue: '',
            Name: this.selectedLevel > 0 ? item.ShortName + '*' : item.ShortName,
            PlanId: this.selectedLevel,
          }));
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.loadingInputs = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
